import { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from '@betty/toolkit/state';
import { StackNavigationProp, useNavigation } from '@betty/navigation';
import { logError } from '@betty/toolkit/logging';
import { Button, InfoImage } from '@betty/moonlight';
import { openChat } from '@betty/chat/state';
import {
  HandlersConfig,
  WithdrawEmailVerificationProps,
} from './WithdrawEmailVerification.types';
import useEmailVerification from '../../hooks/useEmailVerification';
import PaymentService from '../../services/PaymentService/PaymentService';
import WithdrawEmailVerificationSkeleton from './WithdrawEmailVerificationSkeleton';

function WithdrawEmailVerification({
  token,
  close,
}: WithdrawEmailVerificationProps) {
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { data, dataState } = useEmailVerification(token);
  const status = dataState === 'rejected' ? 'Error' : data?.result;

  const resendEmail = useCallback(
    async (email: string) => {
      try {
        setLoading(true);

        await PaymentService.resendInteracEmail({ data: { email } });

        close();
      } catch (error) {
        logError('Send Interac email error', error);
      } finally {
        setLoading(false);
      }
    },
    [close],
  );

  const primaryHandlers = useMemo<HandlersConfig>(
    () => ({
      Verified: () => {
        navigation.navigate('Withdraw');

        close();
      },
      Expired: () => data && resendEmail(data.email),
      Error: () => {
        dispatch(openChat());
      },
    }),
    [data, resendEmail, navigation, dispatch, close],
  );

  const secondaryHandlers = useMemo<HandlersConfig>(
    () => ({
      Verified: close,
      Expired: () => {
        dispatch(openChat());
      },
      Error: close,
    }),
    [dispatch, close],
  );

  if (dataState === 'idle' || dataState === 'pending') {
    return <WithdrawEmailVerificationSkeleton />;
  }

  return (
    status && (
      <InfoImage
        image={status === 'Verified' ? 'common.success' : 'common.error'}
        titleNS="wallet"
        titleKey={`interacEmailVerification${status}Title`}
        descriptionNS="wallet"
        descriptionKey={`interacEmailVerification${status}Description`}
      >
        <Button
          locNS="wallet"
          locKey={`interacEmailVerification${status}PrimaryButton`}
          size="large"
          width="100%"
          loading={loading}
          onPress={primaryHandlers[status]}
        />
        <Button
          variant="secondary"
          locNS="wallet"
          locKey={`interacEmailVerification${status}SecondaryButton`}
          size="large"
          width="100%"
          onPress={secondaryHandlers[status]}
        />
      </InfoImage>
    )
  );
}

export default memo(WithdrawEmailVerification);
